/**
 * Unsaved-changes (dirty) detection for the in-progress order page.
 *
 * `OrderInProgress` persists edits to the lab only when the user presses
 * "Send Updates". That button is enabled by diffing the current canonical
 * state against a baseline captured on fetch / hydrate / last successful send.
 *
 * GEN-3393 follow-up: the legacy implementation only diffed the MOTHER and
 * FATHER comparators, so a consent / demographic edit on a NON-parent
 * comparator (sibling, twin, legal guardian, …) — or on the proband's own
 * consent — updated Redux but never flipped a dirty flag, leaving "Send
 * Updates" disabled and the change un-flushable to IMDB. These pure helpers
 * serialize ALL comparators and the patient consent block so any such edit is
 * detected. They are deliberately free of React/store dependencies so the
 * regression is unit-testable in isolation.
 */
import type {
  CanonicalClinical,
  CanonicalFamily,
  CanonicalGuardianContact,
  CanonicalOrderPayload,
  CanonicalPatient,
  Comparator,
} from '@/ordering/canonicalOrder'
import { type CategorizedHPOOption, normalizePhenotypicTrait } from '@/types/RequisitionFormTypes'

/**
 * Stable, order-independent signature for a comparator's HPO terms.
 *
 * Comparator phenotypes are written by `ComparatorModal` as
 * `HPOTerm = { id, name, hpoCode, type }`, where `type` is the editable
 * Key / Additional / Pertinent-Negative classification. The signature must
 * include `type` (and the descriptive fields) so changing only a term's type
 * flips the dirty flag — reading just `id` (or a never-present `label`) would
 * silently miss that edit and leave "Send Updates" disabled. The `name`/`type`
 * fall back to the legacy `CategorizedHPOOption` (`label`/`category`) shape so
 * either payload shape round-trips defensively.
 */
export function serializeComparatorHpoSig(terms: unknown): string {
  if (!Array.isArray(terms)) return '[]'
  const rows = terms
    .map((raw) => {
      const t = raw as {
        id?: string
        name?: string
        label?: string
        hpoCode?: string
        type?: string
        category?: string
      }
      return {
        id: String(t?.id ?? '').trim(),
        name: String(t?.name ?? t?.label ?? '').trim(),
        hpoCode: String(t?.hpoCode ?? '').trim(),
        type: String(t?.type ?? t?.category ?? '').trim(),
      }
    })
    .sort((a, b) =>
      `${a.id}\t${a.name}\t${a.hpoCode}\t${a.type}`.localeCompare(`${b.id}\t${b.name}\t${b.hpoCode}\t${b.type}`)
    )
  return JSON.stringify(rows)
}

/**
 * Full, stable serialization of a single comparator row for dirty detection.
 *
 * Includes the row identity (`relationship` / `id`) plus every editable field
 * — demographics, specimen, clinical note, phenotype, HPO terms, and the
 * three secondary-consent toggles — so a consent edit on ANY comparator
 * (parent or not) changes the resulting string.
 */
export function serializeComparatorRow(c: Comparator | null | undefined): string {
  if (!c) return 'null'
  const d = c.demographic
  return JSON.stringify({
    id: (c.id ?? '').trim(),
    relationship: c.relationship ?? '',
    isPrimaryGuardian: c.isPrimaryGuardian === true,
    isTestingComparator: c.isTestingComparator !== false,
    parentFirstName: (d?.firstName ?? '').trim(),
    parentLastName: (d?.lastName ?? '').trim(),
    parentDateOfBirth: (d?.dateOfBirth ?? '').trim(),
    sex: (d?.sex ?? '').trim(),
    parentMRN: (d?.mrn ?? '').trim(),
    parentEmail: (d?.email ?? '').trim(),
    parentPhone: (d?.phone ?? '').trim(),
    collectionType: (c.specimen?.collectionType ?? '').trim(),
    clinicalNote: (c.clinicalNote ?? '').trim(),
    includeIncidentalFindings: c.secondaryConsent?.includeIncidentalFindings ?? null,
    newYorkSampleRetentionOptIn: c.secondaryConsent?.newYorkSampleRetentionOptIn === true,
    researchContactConsent: c.secondaryConsent?.researchContactConsent === true,
    phenotypicTrait: normalizePhenotypicTrait(c.phenotypicTrait ?? undefined),
    hpoTermsSig: serializeComparatorHpoSig(c.hpoTerms),
    specimenId: c.specimen?.specimenId ?? null,
    demographicId: d?.demographicId ?? null,
  })
}

/**
 * Stable, order-independent serialization of the entire `comparators[]` array.
 *
 * Each row is serialized then the rows are sorted, so reordering the array
 * does not register as a change while adding, removing, or editing any row
 * (including consent on a non-parent comparator) does.
 */
export function serializeComparatorsBaseline(
  comparators: ReadonlyArray<Comparator | null | undefined> | null | undefined
): string {
  if (!Array.isArray(comparators)) return '[]'
  const rows = comparators
    .filter((c): c is Comparator => c != null)
    .map(serializeComparatorRow)
    .sort((a, b) => a.localeCompare(b))
  return JSON.stringify(rows)
}

/**
 * Patient (proband) consent snapshot for dirty detection: the three consent
 * dimensions a provider can change post-submit via the Patient Consent modal.
 */
export function serializePatientConsentBaseline(patient: CanonicalPatient | null | undefined): string {
  return JSON.stringify({
    incidentalFindingsSelection: patient?.incidentalFindingsSelection ?? null,
    newYorkSampleRetentionOptIn: patient?.newYorkSampleRetentionOptIn ?? null,
    researchContactConsent: patient?.researchContactConsent ?? null,
  })
}

/**
 * Patient (proband) demographics snapshot for dirty detection: the fields the
 * post-submit demographics editor (`NewOrderModal` edit variant) can change —
 * name, DOB, sex, MRN. Kept separate from {@link serializePatientConsentBaseline}
 * (which tracks the consent block) so a demographics edit flips its own dirty
 * flag and enables "Send Updates" without conflating the two proband sections.
 * The DOB is compared on its date portion only, matching how the editor seeds /
 * writes it (`yyyy-mm-dd`), so a stored value with a time component does not read
 * as a phantom change.
 */
export function serializePatientDemographicsBaseline(patient: CanonicalPatient | null | undefined): string {
  return JSON.stringify({
    firstName: (patient?.firstName ?? '').trim(),
    lastName: (patient?.lastName ?? '').trim(),
    middleName: (patient?.middleName ?? '').trim(),
    dateOfBirth: (patient?.dateOfBirth ?? '').trim().slice(0, 10),
    sex: (patient?.sex ?? '').trim(),
    mrn: (patient?.mrn ?? '').trim(),
  })
}

/** Guardian-contact snapshot for dirty detection. */
export function serializeGuardian(g: CanonicalGuardianContact | null | undefined): string {
  if (!g) {
    return 'null'
  }
  return JSON.stringify({
    contactNotAvailable: g.contactNotAvailable ?? false,
    firstName: g.firstName ?? null,
    lastName: g.lastName ?? null,
    dateOfBirth: g.dateOfBirth ?? null,
    email: g.email ?? null,
    phone: g.phone ?? null,
    relationshipToPatient: g.relationshipToPatient ?? null,
  })
}

/** Clinical-summary snapshot (note + differential + prior genetic testing) for dirty detection. */
export function serializeClinicalBaseline(c: CanonicalClinical | null | undefined): string {
  return JSON.stringify({
    clinicalNote: (c?.clinicalNote ?? '').trim(),
    differentialDiagnosis: (c?.differentialDiagnosis ?? '').trim(),
    priorGeneticTesting: (c?.priorGeneticTesting ?? '').trim(),
  })
}

/** Family-history snapshot (summary + relative flags) for dirty detection. */
export function serializeFamilyBaseline(f: CanonicalFamily | null | undefined): string {
  return JSON.stringify({
    familyHistory: (f?.familyHistory ?? '').trim(),
    hasParent: Boolean(f?.hasParent),
    hasGrandparent: Boolean(f?.hasGrandparent),
    hasSibling: Boolean(f?.hasSibling),
    hasCousin: Boolean(f?.hasCousin),
  })
}

/** Sequencing selection snapshot (test type, order type, collection) for dirty detection. */
export function serializeSequencingBaseline(c: CanonicalOrderPayload | null): string {
  return JSON.stringify({
    t: c?.order?.selectedOption ?? '',
    ot: c?.orderType ?? '',
    col: c?.patient?.collectionType ?? '',
  })
}

/** Stable, order-independent signature for the indication phenotype set. */
export function serializeIndicationsBaseline(terms: CategorizedHPOOption[] = []): string {
  return JSON.stringify(
    terms
      .map((term) => ({
        id: term.id,
        label: term.label,
        category: term.category,
      }))
      .sort((a, b) => `${a.id}-${a.category}-${a.label}`.localeCompare(`${b.id}-${b.category}-${b.label}`))
  )
}

/** Serialized baselines for the per-section dirty checks captured on fetch/hydrate/send. */
export interface CanonicalBaselines {
  indications: string
  guardian: string
  clinical: string
  family: string
  comparators: string
  patientConsent: string
  patientDemographics: string
  sequencing: string
}

/**
 * Single source of truth for capturing the per-section dirty-detection baselines
 * from a canonical order. Used wherever a new "last known sent" state is
 * established (initial fetch, backend hydrate, successful Send Updates) so the
 * section→serializer mapping lives in exactly one place.
 */
export function captureCanonicalBaselines(canonical: CanonicalOrderPayload | null | undefined): CanonicalBaselines {
  return {
    indications: serializeIndicationsBaseline(canonical?.reason?.clinicalDiagnosis ?? []),
    guardian: serializeGuardian(canonical?.guardianContact),
    clinical: serializeClinicalBaseline(canonical?.clinical),
    family: serializeFamilyBaseline(canonical?.family),
    comparators: serializeComparatorsBaseline(canonical?.comparators),
    patientConsent: serializePatientConsentBaseline(canonical?.patient),
    patientDemographics: serializePatientDemographicsBaseline(canonical?.patient),
    sequencing: serializeSequencingBaseline(canonical ?? null),
  }
}

/**
 * Whether the unsaved-changes navigation guard should block the user from
 * leaving the in-progress order page.
 *
 * A guard fires only for edits that could still be pushed to the lab. Three
 * states suppress it:
 *   - `hasUnsentChanges` is false — nothing to lose.
 *   - `isUpdatingOrder` — a Send Updates round-trip is in flight; the edits are
 *     being persisted, so blocking would be a false alarm.
 *   - `isCancelled` — the order is terminal. "Send Updates" is gone once an
 *     order is cancelled, so its unsent local edits can never reach the lab and
 *     navigating back to the workspace must not prompt about losing them.
 */
export function shouldBlockUnsavedChangesNav(args: {
  hasUnsentChanges: boolean
  isUpdatingOrder: boolean
  isCancelled: boolean
}): boolean {
  return args.hasUnsentChanges && !args.isUpdatingOrder && !args.isCancelled
}

/**
 * Whether the (autosaved) clinical summary carries characters outside the
 * server free-text allowlist, so "Send Updates" must be blocked.
 *
 * The Clinical Summary modal autosaves as the user types — including any
 * disallowed characters — into `clinical.clinicalNote` /
 * `clinical.differentialDiagnosis`. provider-api's `ClinicalInfo.description`
 * enforces the same allowlist and would reject the update, so this guards the
 * send path. Kept free of React/store deps so it is unit-testable in isolation.
 *
 * `isValidFreeTextFn` is injected (defaulting to the shared validator) to keep
 * this module dependency-light and the check trivially mockable in tests.
 */
export function clinicalSummaryHasDisallowedChars(
  clinical: CanonicalClinical | null | undefined,
  isValidFreeTextFn: (value: string | null | undefined) => boolean
): boolean {
  return (
    !isValidFreeTextFn(clinical?.clinicalNote) ||
    !isValidFreeTextFn(clinical?.differentialDiagnosis) ||
    !isValidFreeTextFn(clinical?.priorGeneticTesting)
  )
}
